<script setup>
import { defineProps, onMounted, ref, defineEmits, onBeforeUnmount, watch } from "vue";
import FirstName from "@/components/form-fields/first-name/Index.vue";
import LastName from "@/components/form-fields/last-name/Index.vue";
import Address from "@/components/form-fields/address/Index.vue";
import Phone from "@/components/form-fields/dealer-phone/Index.vue";
import StateProvince from "@/components/form-fields/state-province/Index.vue";
import City from "@/components/form-fields/city/Index.vue";
import PostalCode from "@/components/form-fields/postal-code/Index.vue";
import Country from "@/components/form-fields/country/Index.vue";
import Others from "@/components/form-fields/others/Index.vue";
import { isPhonePortrait } from '@/hooks/common/useUtils';
import { useCustomer } from '@/hooks/shopify-hooks/useCustomer';
import { getCountry, getProvince } from '@/hooks/shopify-hooks/useShopifyAddressData';
import { 
	useCsrFormValidator,
} from "@/hooks/common/useCsrFormValidator";
import { useChangeCsrState } from "@/hooks/common/useChangeCsrState";

const props = defineProps({
  stateKey: {
    type: String,
    default: "",
  },
  config: {
    type: Object,
    default: null
  },
  customer: {
    type: Object,
    default: null
  },
  editAddress: {
    type: Object,
    default: null
  }
});

// 表单组件
const firstNameInput = ref(null);
const lastNameInput = ref(null);
const addressInput = ref(null);
const address2Input = ref(null);
const countryInput = ref(null);
const phoneNumberInput = ref(null);
const stateInput = ref(null);
const cityInput = ref(null);
const zipCodeInput = ref(null);

const isDefaultAddress = ref(false); // 是否默认地址勾选
const showPhoneNav = ref(false); // 移动端头部导航显示
const provincesOptions = ref([]); // state/provinces的下拉数据
const selectAddressAuto = ref({}); // address自动填充后联动改变的数据存储
const isLoading = ref(false);
const countyDefaultValue = ref(''); // 默认国家值
const isMx = ref(false); // 是否是墨西哥站点
const countryShow = ref(false);
const stateShow = ref(false);

const emit = defineEmits(['close', 'cancel', 'loading']);

const site = document.body.dataset.originalSite || 'us';
isMx.value = site === 'mx';
let countriesData = [];


const getProvinceOption = async () => {
  countriesData = await getCountry();
  if (countriesData.length) {
   const { id: countryId } = countriesData.find(item => item.code.toLowerCase() === site);
   
   const provinces = await getProvince(countryId)
    if (provinces.length) {
      provincesOptions.value = provinces.map((item) => ({
        label: item.name,
        value: item.code,
      }));
    }
  }
}

onMounted(async () => {
  showPhoneNav.value = isPhonePortrait();

  await getProvinceOption();
  const targetCountryIndex = countriesData.findIndex(item => item.code.toLowerCase() === site) || 0;
  countyDefaultValue.value = countriesData[targetCountryIndex]?.name;
  countryShow.value = true;
  stateShow.value = true;

  // 导入谷歌地图
  if (site === 'us') {
    window.igws.$googleMap.importGoogleMapSource({
      libraries: 'geometry,places'
    });
    if (window.google) {
      // 美国站点才启用google自动填写地址功能
      initMap();
    }
  }

  isDefaultAddress.value = props.editAddress.isDefault;
})

onBeforeUnmount(async () => {
  // 清除initMap
  window.initMap = null;
})

const initMap = () => {
  const inputAutocomplete = document.getElementById('maps-autocomplete').querySelector('.md-input__inner');
    /* AUTOCOMPLETE */
  // eslint-disable-next-line
  const autocomplete = new google.maps.places.Autocomplete(inputAutocomplete, {
    types: ['address']
  });
  // 限制是美国的地址自动填写
  autocomplete.setComponentRestrictions({'country': site});
  
  // 监听address改变事件，联动填写state、city、code
  // eslint-disable-next-line
  google.maps.event.addListener(autocomplete, 'place_changed', function() {  
    const place = autocomplete.getPlace();
    let postcode = '', city = '', province = '', address1 = '';
    if (!(place?.address_components || []).length) {
      return;
    }
    for (const placeNode of place.address_components) {
      const addressType = placeNode.types[0];
      // 这里参考MAC的取值
      switch (addressType) {
        case 'street_number':
          address1 = `${placeNode.long_name} ${address1}`;
          break;
        case 'route':
          address1 += placeNode.long_name;
          break;
        case 'postal_code':
          postcode = `${placeNode.long_name}${postcode}`;
          break;
        case 'postal_code_suffix':
          postcode = `${postcode}-${placeNode.long_name}`;
          break;
        case 'locality':
          city = placeNode.long_name;
          break;
        case 'administrative_area_level_1':
          province = placeNode.short_name;
          break;
      }
    }
    console.log('--province', place.address_components)
    selectAddressAuto.value = {
      address1: address1,
      zip: postcode,
      city,
      province
    }
    // 更改state、city、code值
    useChangeCsrState([addressInput, stateInput, cityInput, zipCodeInput], selectAddressAuto.value);
  });
}

window.initMap = initMap;


const handleChangeDefaultAddress = () => {
  isDefaultAddress.value = !isDefaultAddress.value; 
}

const clickCancel = () => {
  emit('cancel');
}

const submitForm = async () => {
  if (isLoading.value) {
    return false;
  }
  useCsrFormValidator([firstNameInput, lastNameInput, addressInput, stateInput, cityInput, zipCodeInput, phoneNumberInput, address2Input]).then(async (res) => {
    const addAddressInput = {
      ...res,
      country: site.toUpperCase(),
    }
    const { addAddress, updateAddress } = useCustomer();
    const addressId = props.editAddress.id;
    isLoading.value = true
    try {
      // 更新地址
      let res = null;
      if (addressId !== -1) {
        res = await updateAddress(addAddressInput, addressId, isDefaultAddress.value);
      } else {
        // 新增地址
        res = await addAddress(addAddressInput, isDefaultAddress.value);
      }
      isLoading.value = false;
      emit('loading', false);
      console.log('地址返回', res)
      if (res?.userErrors?.length > 0 || res?.customerUserErrors?.length > 0) {
        const errorTxt = res?.userErrors[0].code || res?.customerUserErrors[0].code;
        window.igws.$toast.open({
          status: "error",
          text: errorTxt,
        });
      } else {
        emit('close');
      }
    } catch(e) {
      isLoading.value = false;
      console.error(e);
    }
  });
}

watch(() => isLoading.value, (value) => {
  emit('loading', value);
})

</script>

<template>
  <div :class="{'form-v3-ecommerce': true, 'csr-account-form': true}">
    <div class="csr-account-form__nav-title" v-if="showPhoneNav" @click="() => clickCancel()">
      <div class="csr-account__back__icon"></div>
      {{ config.address.title }}
    </div>
    <form @submit.prevent="submitForm">
      <div class="csr-account-form__field">
        <FirstName
          ref="firstNameInput"
          :data-conf="{ 
            label: config.address.firstnameLabel, 
            placeholder: config.address.firstnamePlaceholder, 
            required: true,
            name: 'firstName',
            defaultValue: editAddress?.firstName,
            defErrorMsg: config.requiredFieldsPrompt,
          }"
        />
        <LastName
          ref="lastNameInput"   
          :data-conf="{ 
            label: config.address.lastnameLabel, 
            placeholder: config.address.lastnamePlaceholder, 
            required: true,
            name: 'lastName',
            defaultValue: editAddress?.lastName,
            defErrorMsg: config.requiredFieldsPrompt,
          }" 
        />
      </div>
      <Address
        id="maps-autocomplete"
        ref="addressInput"
        :data-conf="{ 
          label: config.address.addressLabel, 
          placeholder: config.address.addressPlaceholder, 
          required: true,
          name: 'address1',
          defaultValue: editAddress?.address1,
          defErrorMsg: config.requiredFieldsPrompt,
        }"
      />
      <Address
        v-if="config?.address?.address2Label"
        ref="address2Input"
        :data-conf="{ 
          label: config?.address?.address2Label, 
          placeholder: config?.address?.address2Placeholder, 
          required: true,
          name: 'address2',
          defaultValue: editAddress?.address2,
          defErrorMsg: config?.requiredFieldsPrompt,
        }"
      />
      <div class="csr-account-form__field">
        <Country
          v-if="countryShow"
          ref="countryInput"
          :notRunInit="true"
          :data-conf="{ 
            label: config.address.countryLabel, 
            placeholder: config.address.countryPlaceholder, 
            required: true,
            name: 'country',
            defaultValue: countyDefaultValue,
            defErrorMsg: config.requiredFieldsPrompt,
            disabled: true,
          }"
        />
        <Phone 
          ref="phoneNumberInput"
          :data-conf="{ 
            label: config.address.phoneLabel, 
            placeholder: config.address.phonePlaceholder, 
            required: isMx ? true: false,
            name: 'phone',
            defaultValue: editAddress?.phone,
            defErrorMsg: config.requiredFieldsPrompt,
          }"
        />
      </div>
      <div class="csr-account-form__field">
        <template v-if="stateShow">
          <StateProvince
            v-if="provincesOptions.length > 0"
            ref="stateInput"
            :data-conf="{ 
              label: config.address.stateLabel, 
              placeholder: config.address.statePlaceholder, 
              required: true,
              name: 'province',
              defaultValue: editAddress?.provinceCode,
              defErrorMsg: config.requiredFieldsPrompt,
              dataSource: provincesOptions
            }"
          />
          <Others
            v-else
            ref="stateInput"          
            :data-conf="{ 
              label: config.address.stateLabel,
              placeholder: config.address.statePlaceholder, 
              required: false,
              name: 'province',
              defaultValue: editAddress?.province,
              defErrorMsg: config.requiredFieldsPrompt,
            }"
          />
        </template>
        <City 
          ref="cityInput"
          :data-conf="{ 
            label: config.address.cityLabel, 
            placeholder: config.address.cityPlaceholder, 
            required: true,
            name: 'city',
            defaultValue: editAddress?.city,
            defErrorMsg: config.requiredFieldsPrompt,
          }"
        />
        <PostalCode
          ref="zipCodeInput"
          :data-conf="{ 
            label: config.address.zipCodeLabel, 
            placeholder: config.address.zipCodePlaceholder, 
            required: true,
            name: 'zip',
            defaultValue: editAddress?.zip,
            defErrorMsg: config.requiredFieldsPrompt,
            formatHint: config.address?.zipCodeFormatPrompt,
          }"
        />
      </div>
      <!-- 默认地址编辑时不显示 -->
      <MdCheckbox
        :value="isDefaultAddress"
        @change="handleChangeDefaultAddress"
        v-if="!editAddress.isDefault"
      >
        <span class="csr-account-form__default">
          {{ config.address.setDefaultButton }}
        </span>
      </MdCheckbox>

      <div class="csr-account-form__btn-group">
        <button class="csr-account-form__btn md-button-v2 md-button md-button--primary" type="submit">{{ config.confirmButton }}</button> 
        <button class="csr-account-form__btn md-button-v2 md-button md-button--black" type="button" @click="() => clickCancel()">{{ config.cancelButton }}</button> 
      </div>
    </form>
  </div>
</template>